import { storableError } from '../util/errors';

// ================ Action types ================ //

export const CART_LOAD_REQUEST = 'app/Cart/CART_LOAD_REQUEST';
export const CART_LOAD_SUCCESS = 'app/Cart/CART_LOAD_SUCCESS';
export const CART_LOAD_ERROR = 'app/Cart/CART_LOAD_ERROR';

export const CART_SAVE_REQUEST = 'app/Cart/CART_SAVE_REQUEST';
export const CART_SAVE_SUCCESS = 'app/Cart/CART_SAVE_SUCCESS';
export const CART_SAVE_ERROR = 'app/Cart/CART_SAVE_ERROR';

export const CART_UPDATE_ITEM_REQUEST = 'app/Cart/CART_UPDATE_ITEM_REQUEST';
export const CART_UPDATE_ITEM_SUCCESS = 'app/Cart/CART_UPDATE_ITEM_SUCCESS';
export const CART_UPDATE_ITEM_ERROR = 'app/Cart/CART_UPDATE_ITEM_ERROR';

export const CART_REMOVE_ITEM_REQUEST = 'app/Cart/CART_REMOVE_ITEM_REQUEST';
export const CART_REMOVE_ITEM_SUCCESS = 'app/Cart/CART_REMOVE_ITEM_SUCCESS';
export const CART_REMOVE_ITEM_ERROR = 'app/Cart/CART_REMOVE_ITEM_ERROR';

export const CART_PROMOS_SET = 'app/Cart/CART_PROMOS_SET';
export const CART_BUNDLE_PROMOS_SET = 'app/Cart/CART_BUNDLE_PROMOS_SET';

// ================ Helpers ================ //

const MAX_CODE_LENGTH = 80;

const sanitizePromoCode = raw => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  return s.slice(0, MAX_CODE_LENGTH);
};

const dedupePromoCodes = codes => {
  if (!Array.isArray(codes)) return [];
  const seen = new Set();
  const out = [];
  for (const code of codes) {
    const clean = sanitizePromoCode(code);
    if (!clean) continue;
    const key = clean.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(clean);
  }
  return out;
};

// ================ Reducer ================ //

const initialState = {
  cart: {},
  cartPromoCodes: [],
  cartBundlePromoCodes: [],
  loadInProgress: false,
  loadError: null,
  saveInProgress: false,
  saveError: null,
};

const resolveCartPromoCodes = (state, payload) => {
  if (!payload) return state.cartPromoCodes;
  if (payload.cartPromoCodes !== undefined) {
    return dedupePromoCodes(payload.cartPromoCodes);
  }
  if (payload.cartPromoCode !== undefined) {
    const single = sanitizePromoCode(payload.cartPromoCode);
    return single ? dedupePromoCodes([...(state.cartPromoCodes || []), single]) : [];
  }
  return state.cartPromoCodes;
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CART_LOAD_REQUEST:
      return { ...state, loadInProgress: true, loadError: null };
    case CART_LOAD_SUCCESS:
      return {
        ...state,
        loadInProgress: false,
        cart: payload.cart || {},
        cartPromoCodes: resolveCartPromoCodes(state, payload),
      };
    case CART_LOAD_ERROR:
      return { ...state, loadInProgress: false, loadError: payload };

    case CART_SAVE_REQUEST:
      return { ...state, saveInProgress: true, saveError: null };
    case CART_SAVE_SUCCESS:
      return {
        ...state,
        saveInProgress: false,
        cart: payload.cart || state.cart,
        cartPromoCodes: resolveCartPromoCodes(state, payload),
      };
    case CART_SAVE_ERROR:
      return { ...state, saveInProgress: false, saveError: payload };

    case CART_UPDATE_ITEM_REQUEST:
      return { ...state, saveInProgress: true, saveError: null };
    case CART_UPDATE_ITEM_SUCCESS:
      return {
        ...state,
        saveInProgress: false,
        cart: payload.cart || state.cart,
        cartPromoCodes: resolveCartPromoCodes(state, payload),
      };
    case CART_UPDATE_ITEM_ERROR:
      return { ...state, saveInProgress: false, saveError: payload };

    case CART_REMOVE_ITEM_REQUEST:
      return { ...state, saveInProgress: true, saveError: null };
    case CART_REMOVE_ITEM_SUCCESS:
      return {
        ...state,
        saveInProgress: false,
        cart: payload.cart || state.cart,
        cartPromoCodes: resolveCartPromoCodes(state, payload),
      };
    case CART_REMOVE_ITEM_ERROR:
      return { ...state, saveInProgress: false, saveError: payload };

    case CART_PROMOS_SET:
      return { ...state, cartPromoCodes: dedupePromoCodes(payload.cartPromoCodes) };

    case CART_BUNDLE_PROMOS_SET:
      return { ...state, cartBundlePromoCodes: dedupePromoCodes(payload.cartBundlePromoCodes) };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const cartLoadRequest = () => ({ type: CART_LOAD_REQUEST });
export const cartLoadSuccess = ({ cart, cartPromoCodes, cartPromoCode } = {}) => ({
  type: CART_LOAD_SUCCESS,
  payload: {
    cart: cart || {},
    ...(cartPromoCodes !== undefined ? { cartPromoCodes } : {}),
    ...(cartPromoCode !== undefined ? { cartPromoCode } : {}),
  },
});
export const cartLoadError = e => ({ type: CART_LOAD_ERROR, payload: e, error: true });

export const cartSaveRequest = () => ({ type: CART_SAVE_REQUEST });
export const cartSaveSuccess = ({ cart, cartPromoCodes, cartPromoCode } = {}) => ({
  type: CART_SAVE_SUCCESS,
  payload: {
    cart: cart || {},
    ...(cartPromoCodes !== undefined ? { cartPromoCodes } : {}),
    ...(cartPromoCode !== undefined ? { cartPromoCode } : {}),
  },
});
export const cartSaveError = e => ({ type: CART_SAVE_ERROR, payload: e, error: true });

export const cartUpdateItemRequest = () => ({ type: CART_UPDATE_ITEM_REQUEST });
export const cartUpdateItemSuccess = ({ cart, cartPromoCodes, cartPromoCode } = {}) => ({
  type: CART_UPDATE_ITEM_SUCCESS,
  payload: {
    cart: cart || {},
    ...(cartPromoCodes !== undefined ? { cartPromoCodes } : {}),
    ...(cartPromoCode !== undefined ? { cartPromoCode } : {}),
  },
});
export const cartUpdateItemError = e => ({ type: CART_UPDATE_ITEM_ERROR, payload: e, error: true });

export const cartRemoveItemRequest = () => ({ type: CART_REMOVE_ITEM_REQUEST });
export const cartRemoveItemSuccess = ({ cart, cartPromoCodes, cartPromoCode } = {}) => ({
  type: CART_REMOVE_ITEM_SUCCESS,
  payload: {
    cart: cart || {},
    ...(cartPromoCodes !== undefined ? { cartPromoCodes } : {}),
    ...(cartPromoCode !== undefined ? { cartPromoCode } : {}),
  },
});
export const cartRemoveItemError = e => ({ type: CART_REMOVE_ITEM_ERROR, payload: e, error: true });

// ================ localStorage helpers ================ //

const CART_KEY = 'demo-cart';
const PROMOS_KEY = 'demo-cart-promos';
const LEGACY_PROMO_KEY = 'demo-cart-promo';
const BUNDLE_PROMOS_KEY = 'demo-cart-bundle-promos';

const readLocalCart = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeLocalCart = cart => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    scheduleCartSessionSync();
  } catch {
    // quota exceeded – silently ignore
  }
};

const readLocalPromos = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(PROMOS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return dedupePromoCodes(parsed);
    }
    // Backward-compat: if no array yet, migrate the legacy single-code key.
    const legacy = localStorage.getItem(LEGACY_PROMO_KEY);
    if (legacy && legacy.trim()) {
      const migrated = dedupePromoCodes([legacy.trim()]);
      if (migrated.length > 0) {
        try {
          localStorage.setItem(PROMOS_KEY, JSON.stringify(migrated));
        } catch {
          /* ignore */
        }
      }
      return migrated;
    }
    return [];
  } catch {
    return [];
  }
};

const readLocalBundlePromos = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(BUNDLE_PROMOS_KEY);
    return raw ? dedupePromoCodes(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
};

const writeLocalBundlePromos = codes => {
  if (typeof window === 'undefined') return;
  try {
    const list = dedupePromoCodes(codes);
    if (list.length > 0) {
      localStorage.setItem(BUNDLE_PROMOS_KEY, JSON.stringify(list));
    } else {
      localStorage.removeItem(BUNDLE_PROMOS_KEY);
    }
    scheduleCartSessionSync();
  } catch {}
};

const writeLocalPromos = codes => {
  if (typeof window === 'undefined') return;
  try {
    const list = dedupePromoCodes(codes);
    if (list.length > 0) {
      localStorage.setItem(PROMOS_KEY, JSON.stringify(list));
    } else {
      localStorage.removeItem(PROMOS_KEY);
    }
    // Keep legacy key in sync with the first code for backwards compatibility.
    if (list.length > 0) {
      localStorage.setItem(LEGACY_PROMO_KEY, list[0]);
    } else {
      localStorage.removeItem(LEGACY_PROMO_KEY);
    }
    scheduleCartSessionSync();
  } catch {
    // ignore
  }
};

let cartSessionSyncTimer = null;
const CART_SESSION_SYNC_DEBOUNCE_MS = 2000;

const scheduleCartSessionSync = () => {
  if (typeof window === 'undefined') return;
  if (cartSessionSyncTimer) {
    clearTimeout(cartSessionSyncTimer);
  }
  cartSessionSyncTimer = setTimeout(() => {
    cartSessionSyncTimer = null;
    try {
      const { saveCartSession } = require('../util/api');
      saveCartSession(readLocalCart(), readLocalPromos()).catch(() => {});
    } catch {
      // ignore sync failures
    }
  }, CART_SESSION_SYNC_DEBOUNCE_MS);
};

// ================ Cart normalization ================ //

const RESERVED_CART_ROOT_KEYS = new Set(['promoCode', 'promoCodes']);

export const promoCodesFromCartResponse = raw => {
  if (!raw || typeof raw !== 'object') return undefined;
  if (Array.isArray(raw.promoCodes)) {
    return dedupePromoCodes(raw.promoCodes);
  }
  if (Object.prototype.hasOwnProperty.call(raw, 'promoCode')) {
    const p = raw.promoCode;
    if (p == null || p === '') return [];
    return dedupePromoCodes([String(p)]);
  }
  return undefined;
};

// Back-compat helper (kept for callers that still read a single code).
export const promoCodeFromCartResponse = raw => {
  const codes = promoCodesFromCartResponse(raw);
  if (codes === undefined) return undefined;
  return codes.length > 0 ? codes[0] : null;
};

const normalizeCart = raw => {
  if (!raw || typeof raw !== 'object') return {};

  const entries = Object.entries({ ...raw }).filter(([k]) => !RESERVED_CART_ROOT_KEYS.has(k));
  return entries.reduce((acc, [providerId, providerData]) => {
    const isLegacyFormat = Array.isArray(providerData);
    const itemsList = isLegacyFormat ? providerData : (providerData?.items || []);
    const shippingMethodId = isLegacyFormat ? undefined : providerData?.shippingMethodId;
    const shippingSelections =
      !isLegacyFormat && providerData?.shippingSelections && typeof providerData.shippingSelections === 'object'
        ? { ...providerData.shippingSelections }
        : {};

    const normalizedItems = itemsList.map(it => {
      const rawQuantity = it?.quantity;
      let quantity = 0;
      if (typeof rawQuantity === 'number' && !isNaN(rawQuantity) && rawQuantity > 0) {
        quantity = rawQuantity;
      } else if (typeof rawQuantity === 'string') {
        const parsed = Number(rawQuantity);
        if (!isNaN(parsed) && parsed > 0) quantity = parsed;
      }

      return {
        listingId: (it?.listingId?.uuid || it?.listingId) ?? '',
        quantity,
        deliveryMethod: it?.deliveryMethod || undefined,
        priceVariantName: it?.priceVariantName || undefined,
        shippingMethodId: it?.shippingMethodId || undefined,
      };
    });

    acc[providerId] = {
      shippingMethodId: shippingMethodId || undefined,
      shippingSelections,
      items: normalizedItems,
    };
    return acc;
  }, {});
};

// ================ Thunks ================ //

export const loadCart = () => dispatch => {
  dispatch(cartLoadRequest());
  const cart = normalizeCart(readLocalCart());
  const promos = readLocalPromos();
  const bundlePromos = readLocalBundlePromos();
  dispatch(cartLoadSuccess({ cart, cartPromoCodes: promos }));
  if (bundlePromos.length > 0) {
    dispatch({ type: CART_BUNDLE_PROMOS_SET, payload: { cartBundlePromoCodes: bundlePromos } });
  }
  return Promise.resolve(cart);
};

const mergeCartItem = (cart, providerId, item) => {
  const prevGroup = cart[providerId] || { items: [] };
  const prevItems = prevGroup.items || [];
  const { listingId, quantity, deliveryMethod, priceVariantName } = item;

  const idx = prevItems.findIndex(i => i.listingId === listingId && i.priceVariantName === priceVariantName);
  if (idx >= 0) {
    const updatedItems = [...prevItems];
    const prevItem = updatedItems[idx];
    const prevQty = typeof prevItem.quantity === 'number' && !isNaN(prevItem.quantity) ? prevItem.quantity : 0;
    const newQty = typeof quantity === 'number' && !isNaN(quantity) ? quantity : 0;

    updatedItems[idx] = {
      ...prevItem,
      quantity: prevQty + newQty,
      deliveryMethod: deliveryMethod || prevItem.deliveryMethod,
      shippingMethodId: item.shippingMethodId || prevItem.shippingMethodId || undefined,
    };
    return { ...cart, [providerId]: { ...prevGroup, items: updatedItems } };
  }
  return { ...cart, [providerId]: { ...prevGroup, items: [...prevItems, item] } };
};

export const addToCart = ({ listing, orderData }) => (dispatch, getState) => {
  const providerId = listing?.author?.id?.uuid || listing?.author?.id;
  if (!providerId) return Promise.reject(new Error('Listing author missing'));

  const quantity = Number(orderData?.quantity);
  if (isNaN(quantity) || quantity <= 0) return Promise.reject(new Error('Invalid quantity'));

  const item = {
    listingId: listing?.id?.uuid || listing?.id,
    quantity,
    deliveryMethod: orderData?.deliveryMethod,
    priceVariantName: orderData?.priceVariantName,
    shippingMethodId: orderData?.shippingMethodId,
  };

  const state = getState();
  const currentCart = state.Cart?.cart || {};
  const cartPromoCodes = state.Cart?.cartPromoCodes || [];
  const plainCart = normalizeCart(JSON.parse(JSON.stringify(currentCart)));
  if (!plainCart[providerId]) plainCart[providerId] = { items: [] };
  const nextCart = mergeCartItem(plainCart, providerId, item);

  dispatch(cartSaveRequest());
  writeLocalCart(nextCart);
  dispatch(cartSaveSuccess({ cart: nextCart, cartPromoCodes }));
  return Promise.resolve(nextCart);
};

export const updateCartItemQuantity = ({ listingId, providerId, quantity }) => (dispatch, getState) => {
  const validQuantity = Number(quantity);
  if (isNaN(validQuantity) || validQuantity <= 0) {
    return dispatch(removeFromCart({ listingId, providerId }));
  }

  const state = getState();
  const currentCart = state.Cart?.cart || {};
  const cartPromoCodes = state.Cart?.cartPromoCodes || [];
  const updatedCart = { ...currentCart };
  const providerGroup = updatedCart[providerId] || { items: [] };
  const providerItems = providerGroup.items || [];
  const itemIndex = providerItems.findIndex(it => it.listingId === listingId);

  if (itemIndex >= 0) {
    const updatedItems = [...providerItems];
    updatedItems[itemIndex] = { ...providerItems[itemIndex], quantity: validQuantity };
    updatedCart[providerId] = { ...providerGroup, items: updatedItems };

    dispatch(cartUpdateItemRequest());
    writeLocalCart(updatedCart);
    dispatch(cartUpdateItemSuccess({ cart: updatedCart, cartPromoCodes }));
    return Promise.resolve(updatedCart);
  }
  return Promise.resolve(currentCart);
};

export const updateProviderShippingMethod = ({
  providerId,
  shippingMethodId,
  shippingGroupKey = 'provider',
  applyToProvider = false,
}) => (dispatch, getState) => {
  const state = getState();
  const currentCart = state.Cart?.cart || {};
  const cartPromoCodes = state.Cart?.cartPromoCodes || [];
  const updatedCart = { ...currentCart };
  const providerGroup = updatedCart[providerId] || { items: [] };

  if (providerGroup.items && providerGroup.items.length > 0) {
    const effectiveGroupKey = shippingGroupKey || 'provider';
    const nextSelections = { ...(providerGroup.shippingSelections || {}) };

    if (shippingMethodId) {
      nextSelections[effectiveGroupKey] = shippingMethodId;
    } else {
      delete nextSelections[effectiveGroupKey];
    }

    const cleanedSelections = Object.keys(nextSelections).length > 0 ? nextSelections : undefined;

    let nextGroupData = { ...providerGroup, shippingSelections: cleanedSelections };
    if (applyToProvider || effectiveGroupKey === 'provider') {
      nextGroupData.shippingMethodId = shippingMethodId || undefined;
    }

    updatedCart[providerId] = nextGroupData;

    dispatch(cartUpdateItemRequest());
    writeLocalCart(updatedCart);
    dispatch(cartUpdateItemSuccess({ cart: updatedCart, cartPromoCodes }));
    return Promise.resolve(updatedCart);
  }
  return Promise.resolve(currentCart);
};

export const removeFromCart = ({ listingId, providerId }) => (dispatch, getState) => {
  const state = getState();
  const currentCart = state.Cart?.cart || {};
  const cartPromoCodes = state.Cart?.cartPromoCodes || [];
  const updatedCart = { ...currentCart };
  const providerGroup = updatedCart[providerId] || { items: [] };
  const filteredItems = (providerGroup.items || []).filter(it => it.listingId !== listingId);

  if (filteredItems.length === 0) {
    delete updatedCart[providerId];
  } else {
    updatedCart[providerId] = { ...providerGroup, items: filteredItems };
  }

  dispatch(cartRemoveItemRequest());
  writeLocalCart(updatedCart);
  dispatch(cartRemoveItemSuccess({ cart: updatedCart, cartPromoCodes }));
  return Promise.resolve(updatedCart);
};

export const clearProviderCart = providerId => (dispatch, getState) => {
  const state = getState();
  const currentCart = state.Cart?.cart || {};
  const cartPromoCodes = state.Cart?.cartPromoCodes || [];

  if (!currentCart[providerId]) return Promise.resolve(currentCart);

  const updatedCart = { ...currentCart };
  delete updatedCart[providerId];

  dispatch(cartRemoveItemRequest());
  writeLocalCart(updatedCart);
  dispatch(cartRemoveItemSuccess({ cart: updatedCart, cartPromoCodes }));
  return Promise.resolve(updatedCart);
};

export const setCartPromoCodes = rawCodes => dispatch => {
  const list = dedupePromoCodes(Array.isArray(rawCodes) ? rawCodes : []);
  writeLocalPromos(list);
  dispatch({ type: CART_PROMOS_SET, payload: { cartPromoCodes: list } });
  return Promise.resolve(list);
};

export const addCartPromoCode = rawCode => (dispatch, getState) => {
  const next = sanitizePromoCode(rawCode);
  if (!next) return Promise.resolve(getState().Cart?.cartPromoCodes || []);
  const state = getState();
  const existing = state.Cart?.cartPromoCodes || [];
  const merged = dedupePromoCodes([...existing, next]);
  writeLocalPromos(merged);
  dispatch({ type: CART_PROMOS_SET, payload: { cartPromoCodes: merged } });
  return Promise.resolve(merged);
};

export const addBundleCartPromoCode = rawCode => (dispatch, getState) => {
  const next = sanitizePromoCode(rawCode);
  if (!next) return Promise.resolve(getState().Cart?.cartBundlePromoCodes || []);
  const state = getState();
  const existing = state.Cart?.cartBundlePromoCodes || [];
  const merged = dedupePromoCodes([...existing, next]);
  writeLocalBundlePromos(merged);
  dispatch({ type: CART_BUNDLE_PROMOS_SET, payload: { cartBundlePromoCodes: merged } });
  return Promise.resolve(merged);
};

export const removeCartPromoCode = rawCode => (dispatch, getState) => {
  const target = sanitizePromoCode(rawCode);
  if (!target) return Promise.resolve(getState().Cart?.cartPromoCodes || []);
  const state = getState();
  const existing = state.Cart?.cartPromoCodes || [];
  const targetUpper = target.toUpperCase();
  const next = existing.filter(c => c.toUpperCase() !== targetUpper);
  writeLocalPromos(next);
  dispatch({ type: CART_PROMOS_SET, payload: { cartPromoCodes: next } });
  return Promise.resolve(next);
};

/**
 * Legacy single-code setter kept for backwards compatibility.
 * Passing null/empty clears all promo codes; any other value replaces the list.
 */
export const setCartPromoCode = rawCode => dispatch => {
  const clean = sanitizePromoCode(rawCode);
  return dispatch(setCartPromoCodes(clean ? [clean] : []));
};

/**
 * After login, load the guest cart from localStorage into Redux.
 * localStorage persists across login, so the cart is already there.
 */
export const migrateGuestCartToUser = () => dispatch => {
  return dispatch(loadCart()).then(cart => {
    scheduleCartSessionSync();
    return cart;
  });
};
